import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Loader2, Power } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { code: "", kind: "percent", value: 10, valid_from: "", valid_to: "", min_nights: 0, max_uses: 0, notes: "", active: true };

const AdminDiscountCodes = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/discount-codes`, { headers: authHeaders });
      setItems(data.codes || []);
    } catch {
      toast.error("Impossibile caricare i codici sconto");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || Number(form.value) <= 0) { toast.error("Codice e valore obbligatori"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/discount-codes`, {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value),
        min_nights: Number(form.min_nights) || 0,
        max_uses: Number(form.max_uses) || 0,
      }, { headers: authHeaders });
      toast.success("Codice creato");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => {
    try {
      await axios.patch(`${API}/admin/discount-codes/${row.code}`, { active: !row.active }, { headers: authHeaders });
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Eliminare il codice ${row.code}?`)) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${row.code}`, { headers: authHeaders });
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const inp = "w-full px-3 py-2 rounded-lg bg-white border border-[#0D253A]/15 text-sm";

  return (
    <div data-testid="studio-discount-codes" className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-[#0D253A]/10 p-6 space-y-3">
        <h3 className="font-display text-lg text-[#0D253A]">Nuovo codice sconto</h3>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Codice</label>
          <input data-testid="discount-code-input" type="text" value={form.code} onChange={set("code")} placeholder="AMICI15" className={`${inp} uppercase`} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Tipo</label>
            <select data-testid="discount-kind-select" value={form.kind} onChange={set("kind")} className={inp}>
              <option value="percent">Percentuale (%)</option>
              <option value="amount">Importo fisso (€)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Valore</label>
            <input data-testid="discount-value-input" type="number" step="0.01" min="0" value={form.value} onChange={set("value")} className={inp} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Valido dal</label>
            <input type="date" value={form.valid_from} onChange={set("valid_from")} className={inp} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Valido al</label>
            <input type="date" value={form.valid_to} onChange={set("valid_to")} className={inp} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Min. notti</label>
            <input type="number" min="0" value={form.min_nights} onChange={set("min_nights")} className={inp} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Max utilizzi</label>
            <input type="number" min="0" value={form.max_uses} onChange={set("max_uses")} className={inp} />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-[#5C6773]">Note (interne)</label>
          <input type="text" value={form.notes} onChange={set("notes")} className={inp} placeholder="es. amici e famiglia" />
        </div>
        <button data-testid="discount-create-btn" type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#C86D51] text-white text-sm font-semibold rounded-full hover:bg-[#0D253A] disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crea codice
        </button>
      </form>

      <div className="lg:col-span-3 bg-white rounded-2xl border border-[#0D253A]/10 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg text-[#0D253A]">Codici attivi</h3>
          <span className="text-xs text-[#5C6773]">{items.length} totali</span>
        </div>
        {loading ? (
          <p className="text-sm text-[#5C6773] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</p>
        ) : items.length === 0 ? (
          <p data-testid="discount-empty" className="text-sm text-[#5C6773]">Nessun codice creato.</p>
        ) : (
          <div className="space-y-2">
            {items.map((row) => (
              <div key={row.code} data-testid={`discount-row-${row.code}`} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3 ${row.active ? "border-[#0D253A]/10" : "border-[#0D253A]/10 opacity-60"}`}>
                <div>
                  <p className="font-semibold text-[#0D253A]">{row.code} <span className="text-xs text-[#5C6773] font-normal">· {row.kind === "percent" ? `−${row.value}%` : `−€ ${Number(row.value).toFixed(2)}`}</span></p>
                  <p className="text-xs text-[#5C6773]">
                    {row.valid_from && `dal ${row.valid_from} `}
                    {row.valid_to && `al ${row.valid_to} `}
                    {row.min_nights ? `· min ${row.min_nights} notti ` : ""}
                    {row.max_uses ? `· ${row.used_count || 0}/${row.max_uses} usi ` : row.used_count ? `· ${row.used_count} usi` : ""}
                    {row.notes ? ` · ${row.notes}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`discount-toggle-${row.code}`} onClick={() => toggle(row)} title={row.active ? "Disattiva" : "Attiva"} className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51]">
                    <Power className="w-4 h-4" />
                  </button>
                  <button data-testid={`discount-delete-${row.code}`} onClick={() => remove(row)} title="Elimina" className="p-2 rounded-full border border-[#0D253A]/15 hover:border-[#C86D51] hover:text-[#C86D51]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDiscountCodes;
